import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Form, Modal } from "react-bootstrap";
import { CartFill, CheckCircleFill, InfoCircleFill } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";
import { appearanceOptions, stripeFonts } from "./Controls/StripeAppearance";
import { cents, showMoney } from "./Data/Money";
import { getInitials, avatarColor } from "./Data/Avatar";
import { PaymentMethodResp, SubscriptionParker } from "./Data/ApiTypes";
import { stripePromise } from "./Data/ApiStripe";
import { showDate } from "./Data/Date";
import { pmLabel } from "./UserBillingAddMethodModal";
import { countPaymentMethodSubscriptions } from "./Data/Subscription";
import { createApiService } from "./shared/apiConfig";
import {
    GroupParkerApi,
    ServerRequestsGroupParkerCheckoutResponse,
} from "./shared/api/axios-client";
import { PendingGroupParker } from "./GroupParkerAddModal";
import { LocalDate } from "@js-joda/core";
import { fulfillGroupParkerCheckout } from "./groupParkerFulfill";

const groupParkerApi = createApiService(GroupParkerApi);
const GRADIENT = "linear-gradient(135deg, #e8359a 0%, #c0116e 100%)";
const PINK = "#D61F84";
const PAY_FORM_ID = "group-parker-checkout-pay-form";

/** Discriminated selection before CheckoutAsync. */
type PayChoice =
    | { kind: "saved"; paymentMethodId: string; isAch: boolean }
    | { kind: "newCard" };

type PendingSuccess =
    | { kind: "parkers"; parkers: SubscriptionParker[] }
    | { kind: "refresh" };

interface Props {
    show: boolean;
    onHide: () => void;
    subscriptionId: number;
    isAccountsReceivable?: boolean;
    pending: PendingGroupParker[];
    paymentMethods: PaymentMethodResp[];
    existingPmId: string | undefined;
    maxCreditCardAmountInCents?: number | null;
    onSuccess: (parkers: SubscriptionParker[], options?: { refresh?: boolean }) => void;
}

type Step = "summary" | "paymentMethod" | "pay" | "success";

function isBankPm(pm: PaymentMethodResp): boolean {
    return !!pm.usBankAccount;
}

function isCardPm(pm: PaymentMethodResp): boolean {
    return !!pm.card;
}

function AmountDueBanner({
    total,
    subtotal,
    parkerFees,
    label = "Amount Due",
    feesExcluded = false,
}: {
    total: number;
    subtotal: number;
    parkerFees: number;
    label?: string;
    feesExcluded?: boolean;
}) {
    const showBreakdown = !feesExcluded && parkerFees > 0 && subtotal > 0;
    return (
        <div
            className="rounded-3 px-3 py-3 mb-3"
            style={{ background: GRADIENT, color: "#fff" }}
        >
            <div className="d-flex align-items-start justify-content-between gap-3">
                <div className="d-flex align-items-center gap-2 min-w-0">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 36, height: 36, backgroundColor: "rgba(255,255,255,0.2)" }}
                    >
                        <CartFill size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className="small text-uppercase fw-semibold" style={{ opacity: 0.85, letterSpacing: "0.04em" }}>
                            {label}
                        </div>
                        <div className="fw-bold fs-5">
                            {showMoney(cents(total))}
                            {feesExcluded ? "*" : ""}
                        </div>
                        {feesExcluded && (
                            <div style={{ fontSize: "0.7rem", lineHeight: 1.2, opacity: 0.75, marginTop: 1 }}>
                                *Does not include applicable fees
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showBreakdown && (
                <div className="mt-2 pt-2 border-top" style={{ borderColor: "rgba(255,255,255,0.25) !important", fontSize: "0.82rem" }}>
                    <div className="d-flex justify-content-between gap-2">
                        <span style={{ opacity: 0.9 }}>Parking prorate</span>
                        <span>{showMoney(cents(subtotal))}</span>
                    </div>
                    <div className="d-flex justify-content-between gap-2 mt-1">
                        <span style={{ opacity: 0.9 }}>Fees</span>
                        <span>{showMoney(cents(parkerFees))}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function StepPill({ n, label, active }: { n: number; label: string; active: boolean }) {
    return (
        <span
            className={`d-flex align-items-center gap-1 ${active ? "fw-semibold" : "text-secondary"}`}
            style={active ? { color: PINK } : undefined}
        >
            <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{
                    width: 22,
                    height: 22,
                    fontSize: "0.75rem",
                    background: active ? PINK : "#e9ecef",
                    color: active ? "#fff" : "#6c757d",
                }}
            >
                {n}
            </span>
            {label}
        </span>
    );
}

function CheckoutStepper({ step, showPaidSteps }: { step: Step; showPaidSteps: boolean }) {
    if (!showPaidSteps) {
        return (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.8rem" }}>
                <StepPill n={1} label="Review" active={step === "summary"} />
            </div>
        );
    }

    const reviewActive = step === "summary";
    const paymentActive = step === "paymentMethod";
    const checkoutActive = step === "pay" || step === "success";

    return (
        <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.8rem", flexWrap: "nowrap", overflowX: "auto" }}>
            <StepPill n={1} label="Review" active={reviewActive} />
            <span className="text-secondary">→</span>
            <StepPill n={2} label="Payment" active={paymentActive} />
            <span className="text-secondary">→</span>
            <StepPill n={3} label="Checkout" active={checkoutActive} />
        </div>
    );
}

function PaymentInnerForm({
    onSuccess,
    onError,
    onLoadingChange,
}: {
    onSuccess: () => void;
    onError: (msg: string) => void;
    onLoadingChange: (loading: boolean) => void;
}) {
    const stripe   = useStripe();
    const elements = useElements();

    const handlePay = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        onLoadingChange(true);
        const { error: submitErr } = await elements.submit();
        if (submitErr) {
            onLoadingChange(false);
            return;
        }

        const result = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: "if_required",
        });
        if (result.error) {
            onLoadingChange(false);
            onError(result.error.message ?? "Payment failed. Please try again.");
        } else {
            onSuccess();
        }
    };

    return (
        <form id={PAY_FORM_ID} onSubmit={handlePay}>
            <PaymentElement />
        </form>
    );
}

function SavedPmList({
    savedPms,
    selectedPmId,
    onSelect,
    disabled,
}: {
    savedPms: PaymentMethodResp[];
    selectedPmId: string;
    onSelect: (id: string) => void;
    disabled?: boolean;
}) {
    return (
        <div className="d-flex flex-column gap-2">
            {savedPms.map(pm => {
                const activeCount = countPaymentMethodSubscriptions(pm.subscriptions);
                const isSelected = selectedPmId === pm.paymentMethodId;
                const label = pmLabel(pm);
                const detail = pm.card
                    ? `Expires ${pm.card.expirationMonth}/${pm.card.expirationYear.toString().slice(-2)}`
                    : pm.usBankAccount ? pm.usBankAccount.bank : "";
                return (
                    <div
                        key={pm.paymentMethodId}
                        role="button"
                        onClick={() => !disabled && onSelect(pm.paymentMethodId)}
                        className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                        style={{
                            cursor: disabled ? "default" : "pointer",
                            borderColor: isSelected ? PINK : "#dee2e6",
                            background: isSelected ? "rgba(214,31,132,0.07)" : undefined,
                            opacity: disabled ? 0.85 : 1,
                        }}
                    >
                        <Form.Check
                            type="radio"
                            name="group-checkout-pm"
                            checked={isSelected}
                            disabled={disabled}
                            onChange={() => onSelect(pm.paymentMethodId)}
                            onClick={e => e.stopPropagation()}
                            className="flex-shrink-0"
                        />
                        <div className="flex-grow-1">
                            <div className="fw-semibold" style={{ fontSize: "0.92rem" }}>{label}</div>
                            {detail && (
                                <div className="text-secondary" style={{ fontSize: "0.8rem" }}>{detail}</div>
                            )}
                        </div>
                        <span
                            className="px-2 py-1 rounded-pill fw-semibold flex-shrink-0"
                            style={{
                                fontSize: "0.7rem",
                                background: activeCount > 0 ? "#d1fae5" : "#f1f1f1",
                                color: activeCount > 0 ? "#065f46" : "#888",
                            }}
                        >
                            {activeCount > 0 ? `${activeCount} active` : "Unused"}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function CheckoutSuccessStep({
    parkerCount,
    paidAmount,
    charged,
    onDone,
}: {
    parkerCount: number;
    paidAmount: number;
    charged: boolean;
    onDone: () => void;
}) {
    const label = parkerCount === 1 ? "parker" : "parkers";
    return (
        <div className="d-flex flex-column align-items-center text-center p-4 gap-3">
            <CheckCircleFill size={48} style={{ color: PINK }} />
            <div>
                <div className="fw-semibold fs-5 mb-1">
                    {charged ? "Payment successful!" : "Parkers added!"}
                </div>
                <div className="text-secondary" style={{ fontSize: "0.9rem" }}>
                    {charged
                        ? `${showMoney(cents(paidAmount))} charged. ${parkerCount} ${label} added to your plan.`
                        : `No charge today. ${parkerCount} ${label} added to your plan.`}
                </div>
            </div>
            <Button className="btn-main px-5 mt-1" size="sm" onClick={onDone}>
                Done
            </Button>
        </div>
    );
}

function headerTitle(step: Step): string {
    switch (step) {
        case "summary": return "Review";
        case "paymentMethod": return "Payment";
        case "pay": return "Checkout";
        default: return "Checkout";
    }
}

export function GroupParkerCheckoutModal({
    show,
    onHide,
    subscriptionId,
    isAccountsReceivable = false,
    pending,
    paymentMethods,
    existingPmId,
    maxCreditCardAmountInCents = null,
    onSuccess,
}: Props) {
    const [step, setStep] = useState<Step>("summary");
    const [submitting, setSubmitting] = useState(false);
    const [payLoading, setPayLoading] = useState(false);
    const [payErr, setPayErr] = useState<string | undefined>();
    const [checkoutResp, setCheckoutResp] = useState<ServerRequestsGroupParkerCheckoutResponse | null>(null);
    const [payChoice, setPayChoice] = useState<PayChoice | null>(null);
    const [fulfilledParkers, setFulfilledParkers] = useState<SubscriptionParker[]>([]);
    const [fulfillNeedsRefresh, setFulfillNeedsRefresh] = useState(false);
    const [willAmendOpenInvoice, setWillAmendOpenInvoice] = useState(false);
    const prevShowRef = useRef(false);
    const pendingSuccessRef = useRef<PendingSuccess | null>(null);

    const estimatedSubtotal = useMemo(
        () => pending.reduce((sum, p) => sum + (p.previewSubtotal ?? p.previewPrice ?? 0), 0),
        [pending]
    );
    const needsPayment = !isAccountsReceivable && estimatedSubtotal > 0;

    const creditCardLimitExceeded = maxCreditCardAmountInCents != null
        && estimatedSubtotal > maxCreditCardAmountInCents;
    const hasSavedAch = paymentMethods.some(isBankPm);
    const cardsBlocked = creditCardLimitExceeded && hasSavedAch;
    const paymentBlocked = creditCardLimitExceeded && !hasSavedAch;

    const savedPms = useMemo(() => {
        if (cardsBlocked) return paymentMethods.filter(isBankPm);
        return paymentMethods.filter(pm => isCardPm(pm) || isBankPm(pm));
    }, [paymentMethods, cardsBlocked]);

    const allowNewCard = !cardsBlocked;

    const defaultPayChoice = useMemo((): PayChoice | null => {
        const first = savedPms[0];
        if (first) {
            const matched = existingPmId
                ? savedPms.find(p => p.paymentMethodId === existingPmId)
                : undefined;
            const preferred: PaymentMethodResp = matched ?? first;
            return {
                kind: "saved",
                paymentMethodId: preferred.paymentMethodId,
                isAch: isBankPm(preferred),
            };
        }
        if (allowNewCard) return { kind: "newCard" };
        return null;
    }, [savedPms, existingPmId, allowNewCard]);

    const stripeOpts: StripeElementsOptions | undefined = useMemo(() => {
        if (!checkoutResp?.paymentIntentClientSecret) return undefined;
        return {
            clientSecret: checkoutResp.paymentIntentClientSecret,
            ...(checkoutResp.customerSessionClientSecret
                ? { customerSessionClientSecret: checkoutResp.customerSessionClientSecret }
                : {}),
            appearance: appearanceOptions,
            fonts: stripeFonts,
        };
    }, [checkoutResp?.paymentIntentClientSecret, checkoutResp?.customerSessionClientSecret]);

    const resetCheckoutState = () => {
        setStep("summary");
        setCheckoutResp(null);
        setPayChoice(null);
        setFulfilledParkers([]);
        setFulfillNeedsRefresh(false);
        setWillAmendOpenInvoice(false);
        setPayErr(undefined);
        setSubmitting(false);
        setPayLoading(false);
    };

    useEffect(() => {
        if (show && !prevShowRef.current) {
            pendingSuccessRef.current = null;
            resetCheckoutState();
        }
        prevShowRef.current = show;
    }, [show]);

    useEffect(() => {
        if (!show || !isAccountsReceivable || pending.length === 0) return;
        let cancelled = false;
        const startDate = pending[0].startDate;
        groupParkerApi
            .groupParkerPreview({
                subscriptionId,
                serverRequestsGroupParkerPreviewRequest: {
                    startDate: startDate as any,
                    pendingAssignCount: 0,
                    pendingSameGroupAssignCount: 0,
                },
            })
            .then(r => {
                if (!cancelled) setWillAmendOpenInvoice(!!r.data.willAmendOpenInvoice);
            })
            .catch(() => {
                if (!cancelled) setWillAmendOpenInvoice(false);
            });
        return () => { cancelled = true; };
    }, [show, isAccountsReceivable, subscriptionId, pending]);

    // Entering Payment step: seed selection once.
    useEffect(() => {
        if (step === "paymentMethod" && !payChoice && defaultPayChoice) {
            setPayChoice(defaultPayChoice);
        }
    }, [step, payChoice, defaultPayChoice]);

    const busy = submitting || payLoading;

    const handleClose = () => {
        if (busy) return;
        pendingSuccessRef.current = null;
        onHide();
    };

    const handleDone = () => {
        if (!pendingSuccessRef.current) {
            if (fulfilledParkers.length > 0) {
                pendingSuccessRef.current = { kind: "parkers", parkers: fulfilledParkers };
            } else if (fulfillNeedsRefresh) {
                pendingSuccessRef.current = { kind: "refresh" };
            } else if (checkoutResp?.parkers?.length) {
                pendingSuccessRef.current = {
                    kind: "parkers",
                    parkers: checkoutResp.parkers as unknown as SubscriptionParker[],
                };
            } else {
                pendingSuccessRef.current = { kind: "refresh" };
            }
        }
        onHide();
    };

    const handleExited = () => {
        const pendingSuccess = pendingSuccessRef.current;
        pendingSuccessRef.current = null;
        if (pendingSuccess?.kind === "parkers") {
            onSuccess(pendingSuccess.parkers);
        } else if (pendingSuccess?.kind === "refresh") {
            onSuccess([], { refresh: true });
        }
        resetCheckoutState();
    };

    const clearCheckoutIntent = () => {
        setCheckoutResp(null);
    };

    const updatePayChoice = (next: PayChoice) => {
        setPayChoice(next);
        clearCheckoutIntent();
    };

    const handleReviewContinue = () => {
        if (paymentBlocked) {
            setPayErr(creditCardLimitExceeded
                ? "This amount exceeds the facility card limit. Add a bank account to your billing methods, or contact the facility."
                : "Unable to continue checkout.");
            return;
        }
        setPayErr(undefined);
        if (!needsPayment) {
            void handleZeroChargeConfirm();
            return;
        }
        setPayChoice(defaultPayChoice);
        setStep("paymentMethod");
    };

    const handleZeroChargeConfirm = async () => {
        setSubmitting(true);
        setPayErr(undefined);
        try {
            const resp = await groupParkerApi.groupParkerCheckout({
                subscriptionId,
                serverRequestsGroupParkerCheckoutRequest: {
                    parkers: pending.map(p => ({
                        name: p.name,
                        emailAddress: p.emailAddress,
                        phoneNumber: p.phoneNumber || null,
                        startDate: p.startDate as any,
                        subscriptionGroupId: p.subscriptionGroupId ?? null,
                    })),
                    isAch: false,
                },
            });
            setCheckoutResp(resp.data);
            setFulfilledParkers((resp.data.parkers ?? []) as unknown as SubscriptionParker[]);
            setStep("success");
        } catch {
            toast.error("Checkout failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePaymentContinue = async () => {
        if (!payChoice) return;
        if (payChoice.kind === "saved" && !payChoice.paymentMethodId) return;

        const isAch = payChoice.kind === "saved" && payChoice.isAch;
        setSubmitting(true);
        setPayErr(undefined);
        try {
            const resp = await groupParkerApi.groupParkerCheckout({
                subscriptionId,
                serverRequestsGroupParkerCheckoutRequest: {
                    parkers: pending.map(p => ({
                        name: p.name,
                        emailAddress: p.emailAddress,
                        phoneNumber: p.phoneNumber || null,
                        startDate: p.startDate as any,
                        subscriptionGroupId: p.subscriptionGroupId ?? null,
                    })),
                    isAch,
                },
            });
            setCheckoutResp(resp.data);
            if (resp.data.paymentIntentClientSecret) {
                setStep("pay");
            } else {
                setFulfilledParkers((resp.data.parkers ?? []) as unknown as SubscriptionParker[]);
                setStep("success");
            }
        } catch {
            toast.error("Checkout failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePaySuccess = async () => {
        if (!checkoutResp?.orderId) {
            setStep("success");
            return;
        }
        setPayLoading(true);
        setPayErr(undefined);
        try {
            const result = await fulfillGroupParkerCheckout(checkoutResp.orderId);
            if (result.parkers.length > 0) {
                setFulfilledParkers(result.parkers);
            } else if (result.alreadyFulfilled) {
                setFulfillNeedsRefresh(true);
            }
            setStep("success");
        } catch {
            setPayErr("Payment received but parkers could not be added. Please refresh or contact support.");
        } finally {
            setPayLoading(false);
        }
    };

    const paymentDue = checkoutResp?.price ?? estimatedSubtotal;
    const paymentSubtotal = checkoutResp?.subtotal ?? estimatedSubtotal;
    const paymentParkerFees = checkoutResp?.parkerFees ?? 0;
    const completedParkerCount = fulfilledParkers.length || checkoutResp?.parkers?.length || pending.length;

    const selectedSavedId = payChoice?.kind === "saved" ? payChoice.paymentMethodId : "";
    const newCardSelected = payChoice?.kind === "newCard";

    return (
        <Modal
            show={show}
            onHide={step === "success" ? handleDone : handleClose}
            onExited={handleExited}
            backdrop="static"
            keyboard={!busy}
            centered
            size="lg"
            fullscreen="sm-down"
        >
            {step !== "success" && (
            <Modal.Header closeButton={!busy} className="border-0 pb-0 px-3 pt-3 align-items-start">
                <div className="d-flex flex-column gap-1 flex-grow-1 me-2">
                    <CheckoutStepper step={step} showPaidSteps={needsPayment} />
                    <div className="mt-1">
                        <HeaderText size={6}>{headerTitle(step)}</HeaderText>
                        <div className="text-secondary mt-1" style={{ fontSize: "0.82rem" }}>
                            {pending.length} parker{pending.length !== 1 ? "s" : ""} ready to add
                        </div>
                    </div>
                </div>
            </Modal.Header>
            )}

            {step === "summary" && (
                <>
                    <Modal.Body className="pt-2 px-3">
                        <AmountDueBanner
                            total={estimatedSubtotal}
                            subtotal={estimatedSubtotal}
                            parkerFees={0}
                            feesExcluded={!isAccountsReceivable && estimatedSubtotal > 0}
                            label={isAccountsReceivable ? "Applied to Invoice" : "Due Today"}
                        />

                        <div className="d-flex flex-column gap-2 mb-3">
                            {pending.map(p => (
                                <div
                                    key={p.key}
                                    className="border rounded-3 px-3 py-2 bg-white d-flex align-items-center gap-2 gap-sm-3"
                                    style={{
                                        background: "rgba(214,31,132,0.04)",
                                        borderColor: "rgba(214,31,132,0.25)",
                                        minWidth: 0,
                                    }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            backgroundColor: avatarColor(p.name),
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {getInitials(p.name)}
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-truncate">{p.name}</div>
                                        {p.emailAddress && (
                                            <div
                                                className="text-muted text-truncate"
                                                style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}
                                            >
                                                {p.emailAddress}
                                            </div>
                                        )}
                                        <div
                                            className="text-muted text-truncate"
                                            style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}
                                        >
                                            Start {showDate(LocalDate.parse(p.startDate))}
                                            {!p.isCurrentPeriod
                                                && (p.previewSubtotal ?? p.previewPrice ?? 0) === 0
                                                && " · billed next period"}
                                        </div>
                                    </div>
                                    <div className="fw-semibold flex-shrink-0" style={{ fontSize: "0.85rem" }}>
                                        {(p.previewSubtotal ?? p.previewPrice ?? 0) > 0
                                            ? showMoney(cents(p.previewSubtotal ?? p.previewPrice ?? 0))
                                            : "—"}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {payErr && <Alert variant="danger" className="py-2 small">{payErr}</Alert>}

                        {needsPayment && (
                            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                                <InfoCircleFill className="flex-shrink-0 mt-1" />
                                <span>
                                    Click <b>Continue</b> to choose a payment method
                                </span>
                            </div>
                        )}
                        {!needsPayment && (
                            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                                <InfoCircleFill className="flex-shrink-0 mt-1" />
                                <span>
                                    {isAccountsReceivable
                                        ? willAmendOpenInvoice
                                            ? "No charge today. You'll receive an amended invoice reflecting the updated charges."
                                            : "No charge today. Changes will appear on your next invoice."
                                        : "No charge today. Parkers will be added to your plan."}
                                </span>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 px-3 pb-3 gap-2 w-100">
                        <Button size="sm" className="flex-fill flex-sm-grow-0 px-4" onClick={handleClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            className="btn-main flex-fill flex-sm-grow-0 px-4"
                            onClick={handleReviewContinue}
                            loading={submitting}
                            loadingText="Processing…"
                            disabled={paymentBlocked}
                        >
                            {needsPayment ? "Continue" : "Confirm"}
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {step === "paymentMethod" && (
                <>
                    <Modal.Body className="pt-2 px-3">
                        {cardsBlocked && (
                            <div className="small text-muted px-3 py-2 rounded-3 bg-light border mb-3">
                                This amount requires a saved bank account (ACH).
                            </div>
                        )}
                        {payErr && <Alert variant="danger" className="py-2 small">{payErr}</Alert>}

                        <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3 small" role="alert">
                            <InfoCircleFill className="flex-shrink-0 mt-1" />
                            <span>Choose a payment method. Fees depend on card vs bank.</span>
                        </div>

                        {savedPms.length > 0 && (
                            <>
                                <div className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>Saved</div>
                                <SavedPmList
                                    savedPms={savedPms}
                                    selectedPmId={selectedSavedId}
                                    disabled={busy}
                                    onSelect={id => {
                                        const pm = savedPms.find(p => p.paymentMethodId === id);
                                        if (!pm) return;
                                        updatePayChoice({
                                            kind: "saved",
                                            paymentMethodId: id,
                                            isAch: isBankPm(pm),
                                        });
                                    }}
                                />
                            </>
                        )}

                        {allowNewCard && (
                            <div className="mt-3">
                                <div className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>Or</div>
                                <div
                                    role="button"
                                    onClick={() => !busy && updatePayChoice({ kind: "newCard" })}
                                    className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                                    style={{
                                        cursor: busy ? "default" : "pointer",
                                        borderColor: newCardSelected ? PINK : "#dee2e6",
                                        background: newCardSelected ? "rgba(214,31,132,0.07)" : undefined,
                                    }}
                                >
                                    <Form.Check
                                        type="radio"
                                        name="group-checkout-pm"
                                        checked={newCardSelected}
                                        disabled={busy}
                                        onChange={() => updatePayChoice({ kind: "newCard" })}
                                        className="flex-shrink-0"
                                    />
                                    <div>
                                        <div className="fw-semibold" style={{ fontSize: "0.92rem" }}>New card</div>
                                        <div className="text-secondary" style={{ fontSize: "0.8rem" }}>
                                            Enter card details on the next step
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 px-3 pb-3 gap-2 w-100">
                        <Button
                            size="sm"
                            className="flex-fill flex-sm-grow-0 px-4"
                            onClick={() => {
                                if (busy) return;
                                clearCheckoutIntent();
                                setStep("summary");
                            }}
                            disabled={busy}
                        >
                            ← Back
                        </Button>
                        <Button
                            size="sm"
                            className="btn-main flex-fill flex-sm-grow-0 px-4"
                            onClick={handlePaymentContinue}
                            loading={submitting}
                            loadingText="Preparing…"
                            disabled={!payChoice || busy}
                        >
                            Continue
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {step === "pay" && stripeOpts && checkoutResp && payChoice && (
                <Elements
                    stripe={stripePromise}
                    options={stripeOpts}
                    key={checkoutResp.orderId}
                >
                    <PayConfirmStep
                        payChoice={payChoice}
                        savedPms={savedPms}
                        clientSecret={checkoutResp.paymentIntentClientSecret!}
                        total={paymentDue}
                        subtotal={paymentSubtotal}
                        parkerFees={paymentParkerFees}
                        payErr={payErr}
                        payLoading={payLoading}
                        onPayLoadingChange={setPayLoading}
                        onError={setPayErr}
                        onSuccess={handlePaySuccess}
                        onBack={() => {
                            if (payLoading) return;
                            clearCheckoutIntent();
                            setStep("paymentMethod");
                        }}
                    />
                </Elements>
            )}

            {step === "success" && (
                <CheckoutSuccessStep
                    parkerCount={completedParkerCount}
                    paidAmount={paymentDue}
                    charged={needsPayment}
                    onDone={handleDone}
                />
            )}
        </Modal>
    );
}

function PayConfirmStep({
    payChoice,
    savedPms,
    clientSecret,
    total,
    subtotal,
    parkerFees,
    payErr,
    payLoading,
    onPayLoadingChange,
    onError,
    onSuccess,
    onBack,
}: {
    payChoice: PayChoice;
    savedPms: PaymentMethodResp[];
    clientSecret: string;
    total: number;
    subtotal: number;
    parkerFees: number;
    payErr: string | undefined;
    payLoading: boolean;
    onPayLoadingChange: (loading: boolean) => void;
    onError: (msg: string | undefined) => void;
    onSuccess: () => void;
    onBack: () => void;
}) {
    const stripe = useStripe();
    const selectedPm = payChoice.kind === "saved"
        ? savedPms.find(p => p.paymentMethodId === payChoice.paymentMethodId)
        : undefined;

    const handleSavedPay = async () => {
        if (!stripe || payChoice.kind !== "saved" || payLoading) return;
        onPayLoadingChange(true);
        onError(undefined);
        const result = await stripe.confirmPayment({
            clientSecret,
            confirmParams: {
                payment_method: payChoice.paymentMethodId,
                return_url: window.location.href,
            },
            redirect: "if_required",
        });
        if (result.error) {
            onPayLoadingChange(false);
            onError(result.error.message ?? "Payment failed. Please try again.");
        } else {
            onSuccess();
        }
    };

    return (
        <>
            <Modal.Body className="pt-2 px-3">
                <AmountDueBanner
                    total={total}
                    subtotal={subtotal}
                    parkerFees={parkerFees}
                    label="Amount Due"
                />

                {payErr && <Alert variant="danger" className="py-2 small">{payErr}</Alert>}

                {payChoice.kind === "saved" && selectedPm && (
                    <div className="mb-3">
                        <div className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>Paying with</div>
                        <div className="p-3 rounded-3 border" style={{ borderColor: PINK, background: "rgba(214,31,132,0.07)" }}>
                            <div className="fw-semibold" style={{ fontSize: "0.92rem" }}>{pmLabel(selectedPm)}</div>
                            <div className="text-secondary" style={{ fontSize: "0.8rem" }}>
                                {selectedPm.card
                                    ? `Expires ${selectedPm.card.expirationMonth}/${selectedPm.card.expirationYear.toString().slice(-2)}`
                                    : selectedPm.usBankAccount?.bank ?? "Bank account"}
                            </div>
                        </div>
                    </div>
                )}

                {payChoice.kind === "newCard" && (
                    <>
                        <p className="text-secondary mb-3" style={{ fontSize: "0.88rem" }}>
                            Enter card details to complete payment.
                        </p>
                        <PaymentInnerForm
                            onSuccess={onSuccess}
                            onError={msg => onError(msg)}
                            onLoadingChange={onPayLoadingChange}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 px-3 pb-3 gap-2 w-100">
                <Button size="sm" className="flex-fill flex-sm-grow-0 px-4" onClick={onBack} disabled={payLoading}>
                    ← Back
                </Button>
                {payChoice.kind === "saved" ? (
                    <Button
                        size="sm"
                        className="btn-main flex-fill flex-sm-grow-0 px-4"
                        disabled={payLoading}
                        loading={payLoading}
                        loadingText="Processing…"
                        onClick={handleSavedPay}
                    >
                        Pay Now
                    </Button>
                ) : (
                    <Button
                        type="submit"
                        form={PAY_FORM_ID}
                        size="sm"
                        className="btn-main flex-fill flex-sm-grow-0 px-4"
                        disabled={payLoading}
                        loading={payLoading}
                        loadingText="Processing…"
                    >
                        Pay Now
                    </Button>
                )}
            </Modal.Footer>
        </>
    );
}
